import { processPresetsPolyfill } from './utils'
import type { PresetPolyfillData } from './types'
import type { ILogger } from '@wix/thunderbolt-types'
import { DATA_PRESET_ATTRIBUTE } from './constants'

export const PRESETS_WRAPPER_CLASS = 'wix-presets-wrapper'
const DEBOUNCE_MS = 50

/**
 * DOM mutation observer for builder components polyfill
 * Watches for child node additions in wix-presets-wrapper elements and reapplies polyfill
 */
export class BuilderComponentMutationObserver {
	private readonly getPresetsPolyfillData: () => Record<string, PresetPolyfillData>
	private readonly logger: ILogger
	private mutationObserver: MutationObserver | null = null
	private debounceTimer: number | null = null
	private target: Element | null = null

	constructor(getPresetsPolyfillData: () => Record<string, PresetPolyfillData>, logger: ILogger) {
		this.getPresetsPolyfillData = getPresetsPolyfillData
		this.logger = logger
		this.mutationObserver = new MutationObserver((mutations) => {
			this.handleMutations(mutations)
		})
	}

	/**
	 * Handle mutation events
	 */
	private handleMutations(mutations: MutationRecord[]): void {
		this.logger.interactionStarted('builder-component-mutation-detection')

		try {
			const addedComponentIds: string[] = []

			for (const mutation of mutations) {
				const componentIds = this.getAddedComponentIds(mutation)
				addedComponentIds.push(...componentIds)
			}

			if (addedComponentIds.length > 0) {
				this.scheduleReprocess(addedComponentIds)
			}
		} catch (error) {
			this.logger.captureError(error as Error, {
				tags: { feature: 'builder-component', method: 'handleMutations' },
			})
		} finally {
			this.logger.interactionEnded('builder-component-mutation-detection')
		}
	}

	/**
	 * Schedule polyfill reprocess with debouncing
	 */
	private scheduleReprocess(componentIds: string[]): void {
		if (this.debounceTimer !== null) {
			clearTimeout(this.debounceTimer)
		}

		this.debounceTimer = window.setTimeout(() => {
			this.reprocessPolyfills(componentIds)
			this.debounceTimer = null
		}, DEBOUNCE_MS)
	}

	/**
	 * Get component IDs from added nodes with wix-presets-wrapper class
	 * Component IDs are extracted from classes like "w-comp123"
	 * Skips nodes that already have data-preset attribute (polyfill already applied)
	 */
	private getAddedComponentIds(mutation: MutationRecord): string[] {
		if (!mutation.addedNodes.length) {
			return []
		}

		const componentIds: string[] = []

		for (const node of Array.from(mutation.addedNodes)) {
			if (node instanceof Element && node.classList.contains(PRESETS_WRAPPER_CLASS)) {
				// Skip if polyfill was already applied (has data-preset attribute)
				if (node.hasAttribute(DATA_PRESET_ATTRIBUTE)) {
					continue
				}

				// Extract component ID from class like "w-comp123"
				for (const className of Array.from(node.classList)) {
					if (className.startsWith('w-')) {
						const componentId = className.substring(2) // Remove "w-" prefix
						if (componentId) {
							componentIds.push(componentId)
						}
					}
				}
			}
		}

		return componentIds
	}

	/**
	 * Reprocess all polyfills
	 */
	private reprocessPolyfills(addedComponentIds: string[]): void {
		if (!this.mutationObserver || !this.target) {
			return
		}

		const shouldMeasure = addedComponentIds.length > 0
		let interactionStarted = false

		try {
			this.mutationObserver.disconnect()

			if (shouldMeasure) {
				this.logger.interactionStarted('builder-component-mutation-polyfill', {
					customParams: { componentsCount: addedComponentIds.length },
				})
				interactionStarted = true
			}

			const polyfillData = this.getPresetsPolyfillData()
			processPresetsPolyfill(polyfillData)
		} catch (error) {
			this.logger.captureError(error as Error, {
				tags: { feature: 'builder-component', method: 'reprocessPolyfills' },
			})
		} finally {
			if (interactionStarted) {
				this.logger.interactionEnded('builder-component-mutation-polyfill', {
					customParams: { componentsCount: addedComponentIds.length },
				})
			}
			this.reconnect()
		}
	}

	/**
	 * Reconnect the observer
	 */
	private reconnect(): void {
		if (this.mutationObserver && this.target) {
			this.mutationObserver.observe(this.target, {
				childList: true,
				subtree: true,
			})
		}
	}

	/**
	 * Start observing
	 */
	startObserving(targetElement?: Element): void {
		if (!targetElement && !document.body) {
			return
		}

		this.target = targetElement || document.body

		if (this.mutationObserver) {
			this.mutationObserver.observe(this.target, {
				childList: true,
				subtree: true,
			})
		}
	}

	/**
	 * Dispose and clean up
	 */
	dispose(): void {
		if (this.debounceTimer !== null) {
			clearTimeout(this.debounceTimer)
			this.debounceTimer = null
		}

		if (this.mutationObserver) {
			this.mutationObserver.disconnect()
			this.mutationObserver = null
		}

		this.target = null
	}
}

/**
 * Create and start mutation observer
 */
export const builderComponentPresetsPolyfillMutationObserver = (
	getPresetsPolyfillData: () => Record<string, PresetPolyfillData>,
	logger: ILogger,
	targetElement?: Element
): BuilderComponentMutationObserver => {
	const observer = new BuilderComponentMutationObserver(getPresetsPolyfillData, logger)
	observer.startObserving(targetElement)
	return observer
}
